// islands/KitDiscountClient.tsx
import { useEffect, useState } from 'preact/hooks'
import { invoke } from '$store/runtime.ts'
import * as preact from 'preact'

/** Tipos mínimos usados aqui (com flags de blink) */
type TagColors = {
  textColor?: string
  backgroundColorTag?: string
  onBlink?: boolean
  blinkSpeedMs?: number
  /** 0–100 (porcentagem de “apagado”) */
  blinkDimPercent?: number
}
type PriceSpec = { priceType: string; price: number }
type Offer = { price: number; priceSpecification?: PriceSpec[] }
type ProductVariant = {
  productID: string
  name: string
  image: { url: string; name?: string }[]
  offers?: { offers?: Offer[] }
}
type CatalogProduct = {
  productID: string
  name: string
  isVariantOf: { hasVariant: ProductVariant[] }
}
type AdditionalProp = { name?: string; value?: string; propertyID?: string }
type Product = {
  productID?: string
  name?: string
  additionalProperty?: AdditionalProp[]
  isVariantOf?: { additionalProperty?: AdditionalProp[] }
}

interface Props {
  product: Product
  includeSimilars?: boolean
  tagColors?: TagColors
  className?: string
}

const TAG = '[KitDiscountClient]'

const getListPrice = (priceSpec: PriceSpec[] = []) =>
  priceSpec.find((p) => p.priceType === 'https://schema.org/ListPrice')
    ?.price ?? null

const uniqById = <T extends { productID: string }>(arr: T[]) =>
  arr.filter(
    (v, i, self) => self.findIndex((x) => x.productID === v.productID) === i
  )

const extractKitPropFrom = (additional?: AdditionalProp[]): string | null => {
  if (!Array.isArray(additional)) return null
  const kit = additional.find(
    (p) => p?.name === 'Kit' && typeof p?.value === 'string'
  )
  return kit?.value ?? null
}

const resolveKitMembers = async (product: Product) => {
  const pid = product?.productID

  const fromProduct = extractKitPropFrom(product?.additionalProperty)
  if (fromProduct) {
    const arr = fromProduct
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
    return arr
  }

  const fromParent = extractKitPropFrom(
    product?.isVariantOf?.additionalProperty
  )
  if (fromParent) {
    const arr = fromParent
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
    return arr
  }

  const tryLoaders: Array<{ name: string; call: () => Promise<any> }> = [
    {
      name: 'site.loaders.productDetails',
      call: () => (invoke['site']?.loaders as any)?.productDetails?.({ productID: pid }),
    },
    {
      name: 'site.loaders.product',
      call: () => (invoke['site']?.loaders as any)?.product?.({ id: pid }),
    },
    {
      name: 'site.loaders.vtexProduct',
      call: () => (invoke['site']?.loaders as any)?.vtexProduct?.({ productID: pid }),
    },
  ].filter((x) => typeof x.call === 'function')

  for (const loader of tryLoaders) {
    try {
      const data = await loader.call()

      const maybe =
        extractKitPropFrom(data?.additionalProperty) ||
        extractKitPropFrom(data?.isVariantOf?.additionalProperty) ||
        extractKitPropFrom(data?.product?.additionalProperty) ||
        extractKitPropFrom(data?.product?.isVariantOf?.additionalProperty)

      if (maybe) {
        const arr = maybe
          .split(',')
          .map((v: string) => v.trim())
          .filter(Boolean)
        console.log(`${TAG} ✅ kitProp via ${loader.name}:`, arr)
        return arr
      }
    } catch (err) {
      console.warn(`${TAG} ⚠️ erro chamando ${loader.name}:`, err)
    }
  }

  console.warn(
    `${TAG} ❌ Não foi possível resolver os membros do kit para o productID:`,
    pid
  )
  return []
}

/* ---------- Blink local: só o fundo, sem interferir no absolute/z-10 ---------- */
function BlinkBadgeSpan({
  children,
  bg = '#da1884',
  color = '#ffffff',
  blink = true,
  speedMs = 600,
  dimOpacity = 0, // 0 = apaga total; 0.3 = apaga 70%
  className = '',
  style = {},
}: {
  children: preact.ComponentChildren
  bg?: string
  color?: string
  blink?: boolean
  speedMs?: number
  dimOpacity?: number
  className?: string
  style?: preact.JSX.CSSProperties
}) {
  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @media (prefers-reduced-motion: reduce) {
            ._blinkBg_local_::after { animation: none !important; }
          }
          @keyframes discount-bg-blink-local {
            0%, 49% { opacity: 1; }
            50%, 100% { opacity: var(--dim-op, 0); }
          }
          /* não altere position/z-index do span para não quebrar absolute/z-10 passado via className */
          ._blinkBg_local_{
            background-color: transparent !important; /* fundo real fica no ::after */
            display: inline-block;
            isolation: isolate; /* permite ::after com z-index:-1 ficar atrás só deste span */
          }
          ._blinkBg_local_::after{
            content:"";
            position:absolute;
            inset:0;
            background: var(--bg, #da1884);
            border-radius: inherit;
            z-index:-1;
            pointer-events:none;
            animation: discount-bg-blink-local var(--blink-speed, 600ms) steps(1,end) infinite;
          }
        `,
        }}
      />
      <span
        className={`${className} ${blink ? '_blinkBg_local_' : ''}`}
        style={{
          ['--bg' as any]: bg,
          ['--blink-speed' as any]: `${speedMs}ms`,
          ['--dim-op' as any]: `${Math.max(0, Math.min(1, dimOpacity))}`,
          color,
          backgroundColor: blink ? 'transparent' : bg, // sem blink, usa fundo direto
          ...style,
        }}
      >
        {children}
      </span>
    </>
  )
}
/* ----------------------------------------------------------------------------- */

export default function KitDiscountClient({
  product,
  includeSimilars = true,
  className = '',
  tagColors,
}: Props) {
  const [offPct, setOffPct] = useState<number | null>(null)

  // 1) Se veio tagColors, usa direto; 2) Se não, defaults
  const effectiveBg =
    (tagColors?.backgroundColorTag && String(tagColors.backgroundColorTag)) ||
    '#CD3D82'
  const effectiveFg =
    (tagColors?.textColor && String(tagColors.textColor)) || '#fff'

  // Blink config
  const blinkEnabled = !!tagColors?.onBlink
  const blinkSpeed = tagColors?.blinkSpeedMs ?? 600
  const dimOpacity =
    typeof tagColors?.blinkDimPercent === 'number'
      ? Math.max(0, Math.min(100, tagColors.blinkDimPercent)) / 100
      : 0

  // Aviso útil no console, se estiver caindo em defaults
  if (!tagColors?.backgroundColorTag || !tagColors?.textColor) {
    console.warn(
      `${TAG} ⚠️ Usando DEFAULTS de cor. Verifique se está passando exatamente: { textColor: '#fff', backgroundColorTag: '#CD3D82' }`
    )
  }

  useEffect(() => {
    const run = async () => {
      try {
        const hasCluster = product?.additionalProperty?.some(
          (prop) => prop?.name === 'cluster' && prop?.propertyID === '468'
        )

        if (!hasCluster) {
          console.warn(
            `${TAG} ❌ Produto não está no cluster de KIT; abortando.`
          )
          setOffPct(null)
          return
        }

        const kitValueArray = await resolveKitMembers(product)
        console.log(`${TAG} 🧩 kitValueArray (membros):`, kitValueArray)

        if (!Array.isArray(kitValueArray) || kitValueArray.length === 0) {
          console.warn(`${TAG} ❌ Sem IDs de membros para calcular; abortando.`)
          setOffPct(null)
          return
        }

        const res = await invoke['site'].loaders.kitCatalog({
          productIds: kitValueArray,
        })

        let products: CatalogProduct[] = []
        if (res && Object.keys(res).length > 0) {
          products = Object.values(res) as CatalogProduct[]
        } else {
          console.warn(`${TAG} ❌ kitCatalog vazio/inválido`)
          setOffPct(null)
          return
        }

        let similarVariantsMap: Record<string, ProductVariant[]> = {}
        if (includeSimilars) {
          try {
            const resSimilars = await invoke['site'].loaders.kitSimilars({
              productIds: kitValueArray,
            })
            console.log(`${TAG} 🔄 kitSimilars response:`, resSimilars)
            if (Array.isArray(resSimilars)) {
              const map: Record<string, ProductVariant[]> = {}
              resSimilars.forEach((item: any) => {
                if (item?.productID && item?.variants?.length) {
                  map[item.productID] = item.variants
                }
              })
              similarVariantsMap = map
            }
          } catch (err) {
            console.warn(`${TAG} ⚠️ erro chamando kitSimilars:`, err)
          }
        }

        let subtotalDe = 0
        let subtotalPor = 0

        products.forEach((p) => {
          const base = p?.isVariantOf?.hasVariant ?? []
          const similars = similarVariantsMap[p.productID] ?? []
          const variants = uniqById<ProductVariant>([...base, ...similars])

          const selected = variants[0]
          const offer = selected?.offers?.offers?.[0]
          const listPrice = getListPrice(offer?.priceSpecification as any)
          const salePrice = offer?.price ?? 0

          subtotalDe += listPrice ?? salePrice ?? 0
          subtotalPor += salePrice ?? 0
        })

        if (subtotalDe > 0 && subtotalPor > 0 && subtotalDe > subtotalPor) {
          const pct = Math.round(
            ((subtotalDe - subtotalPor) / subtotalDe) * 100
          )
          console.log(`${TAG} ✅ Desconto calculado:`, pct, '%')
          setOffPct(pct)
        } else {
          console.warn(`${TAG} ❌ Não foi possível calcular desconto válido`)
          setOffPct(null)
        }
      } catch (err) {
        console.error(`${TAG} 💥 Erro inesperado:`, err)
        setOffPct(null)
      }
    }

    run()
  }, [JSON.stringify(product), includeSimilars])

  if (!offPct || offPct <= 0) return null

  return (
    <BlinkBadgeSpan
      bg={effectiveBg}
      color={effectiveFg}
      blink={blinkEnabled}
      speedMs={blinkSpeed}
      dimOpacity={dimOpacity}
      // mantém teu layout/posição original:
      className={`absolute left-0 top-0 z-10 py-1 lg:py-[6px] text-[10px] lg:text-sm font-bold w-auto px-3  ${
        className ?? ''
      }`}
    >
      -{offPct}% OFF
    </BlinkBadgeSpan>
  )
}
